import { Alert, Col, Label, Panel, Row, Well } from 'react-bootstrap';
import { IEmpletAllele } from '@nkr/models/index';
import { AlleleType } from '@nkr/enums/index';
import AlleleDetails from '@nkr/views/swap-management/allele-details';

interface IProps {
    recipientId: number,
    donorId: number,
    recipientAlias: string,
    donorAlias: string,
    recipientAlleles: IEmpletAllele[],
    donorAlleles: IEmpletAllele[]
}

interface IDqMolecule {
    dqb: IEmpletAllele,
    dqa: IEmpletAllele
}

interface IDqPhaseOption {
    key: string,
    molecules: IDqMolecule[],
    mismatch: number
}

const moleculeMismatch = (molecule: IDqMolecule) => molecule.dqb.epletCountMismatch + molecule.dqa.epletCountMismatch;

// DQA1 and DQB1 are inherited together on the same haplotype, so the donor's four
// alleles can only pair up two ways. Each phase is scored on its highest single
// molecule, not on the sum of both molecules.
const buildDqPhaseOptions = (dqbAlleles: IEmpletAllele[], dqaAlleles: IEmpletAllele[]): IDqPhaseOption[] => {
    if (dqbAlleles.length === 0 || dqaAlleles.length === 0) {
        return [];
    }

    let firstDqb = dqbAlleles[0];
    let secondDqb = dqbAlleles.length > 1 ? dqbAlleles[1] : dqbAlleles[0];
    let firstDqa = dqaAlleles[0];
    let secondDqa = dqaAlleles.length > 1 ? dqaAlleles[1] : dqaAlleles[0];

    let options = [
        [{ dqb: firstDqb, dqa: firstDqa }, { dqb: secondDqb, dqa: secondDqa }],
        [{ dqb: firstDqb, dqa: secondDqa }, { dqb: secondDqb, dqa: firstDqa }]
    ].map((molecules): IDqPhaseOption => ({
        key: molecules.map(molecule => `${molecule.dqb.hlaString}+${molecule.dqa.hlaString}`).sort().join('/'),
        molecules,
        mismatch: Math.max(...molecules.map(moleculeMismatch))
    }));

    // A homozygous donor produces the same pairing twice.
    return options
        .filter((option, index) => options.findIndex(other => other.key === option.key) === index)
        .sort((option1, option2) => option2.mismatch - option1.mismatch);
}

const mismatchLabel = (mismatch: number) => mismatch >= 15 ? 'danger' : (mismatch >= 9 ? 'warning' : 'success');

const MolecularMismatchRisk = (props: IProps) => {
    let alertMessage = null;
    let drAlleles = props.donorAlleles.filter(allele => allele.isDr);
    let dqaAlleles = props.donorAlleles.filter(allele => /^DQA/.test(allele.hlaString));
    let dqbAlleles = props.donorAlleles.filter(allele => /^DQB/.test(allele.hlaString));
    let dqPhaseOptions = buildDqPhaseOptions(dqbAlleles, dqaAlleles);

    let topDrAllele = drAlleles.length === 0 ? null
        : drAlleles.reduce((highest, allele) => allele.epletCountMismatch > highest.epletCountMismatch ? allele : highest);

    let highestDrSmem = topDrAllele != null ? topDrAllele.epletCountMismatch : -1;
    let highestDqSmem = dqPhaseOptions.length === 0 ? -1 : dqPhaseOptions[0].mismatch;
    let hasTwoDqOptions = dqPhaseOptions.length > 1 && dqPhaseOptions[0].mismatch !== dqPhaseOptions[1].mismatch;

    if (highestDqSmem >= 15) {
        alertMessage = <Alert bsStyle="danger">HIGH EPLET MM</Alert>;
    } else if (highestDrSmem >= 7 || highestDqSmem >= 9) {
        alertMessage = <Alert bsStyle="warning">MEDIUM EPLET MM</Alert>;
    } else if (highestDrSmem > 0 || highestDqSmem > 0) {
        alertMessage = <Alert bsStyle="success">LOW EPLET MM</Alert>;
    } else {
        alertMessage = <Alert bsStyle="success">ZERO EPLET MM</Alert>;
    }

    return (
        <div className='row'>
            <div className='col-xs-12'>
                <Panel bsStyle="primary">
                    <Panel.Heading>
                        HLA-DR/DQ Molecular Mismatch Risk
                    </Panel.Heading>
                    <Panel.Body>
                        <div>
                            <Row>
                                <Col xs={12} md={12} lg={4} id="risk">
                                    {alertMessage}
                                    {(hasTwoDqOptions) &&
                                        (<p>The exact DQA1-DQB1 allele combination cannot be unequivocally established. Please review the two phase options listed to determine the most probable combination based on the individual's race and published DQA1-DQB1 linkage disequilibrium frequencies.</p>)
                                    }
                                </Col>
                                <Col xs={12} md={6} lg={4}>
                                    <div>
                                        <strong>Top HLA-DRB</strong>
                                    </div>
                                    <div id="DrHigh">
                                        {topDrAllele != null && (
                                            <AlleleDetails alleles={topDrAllele} dismiss={true} allelesType={AlleleType.DonorAllele} isAdmin={false} placement="top" />
                                        )}
                                    </div>
                                    <div>
                                        <strong>HLA-DQA/DQB</strong>
                                    </div>
                                    <div id="DqHigh">
                                        {dqPhaseOptions.map((option) => (
                                            <Well bsSize="small" key={option.key}>
                                                <Row>
                                                    <Col xs={4}>
                                                        {option.molecules.map((molecule, index) => (
                                                            <AlleleDetails key={index} alleles={molecule.dqb} dismiss={true} allelesType={AlleleType.DonorAllele} isAdmin={false} placement='top' />
                                                        ))}
                                                    </Col>
                                                    <Col xs={4}>
                                                        {option.molecules.map((molecule, index) => (
                                                            <AlleleDetails key={index} alleles={molecule.dqa} dismiss={true} allelesType={AlleleType.DonorAllele} isAdmin={false} placement='top' />
                                                        ))}
                                                    </Col>
                                                    <Col xs={4}>
                                                        <strong>Mismatch:&nbsp;</strong>
                                                        <Label bsStyle={mismatchLabel(option.mismatch)}>{option.mismatch}</Label>
                                                    </Col>
                                                </Row>
                                            </Well>
                                        ))}
                                    </div>
                                </Col>
                                <Col xs={12} md={6} lg={4} style={{marginTop:15}}>
                                    <ul>
                                        <li>
                                            <strong>Low Eplet MM:</strong> DR &lt; 7 and DQ &lt; 9
                                        </li>
                                        <li>
                                            <strong>Med Eplet MM:</strong>
                                            <ul>
                                                <li>DR &ge; 7 and DQ &le; 14</li>
                                                <li>or, DR 0-6 and DQ 9-14</li>
                                            </ul>
                                        </li>
                                        <li>
                                            <strong>High Eplet MM:</strong> DR 0-22 and DQ 15-31
                                        </li>
                                    </ul>
                                </Col>
                            </Row>
                        </div>
                    </Panel.Body>
                </Panel>
            </div>
        </div>
    );
}

export default MolecularMismatchRisk;